import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { LogOut, User as UserIcon, Recycle, LayoutDashboard, PlusCircle, ClipboardList, Users, FileBarChart2, Settings } from "lucide-react";

const CITIZEN_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/submissions/new", label: "Nuovo conferimento", icon: PlusCircle },
];
const OPERATOR_NAV = [
  { to: "/operator", label: "Coda conferimenti", icon: ClipboardList },
  { to: "/admin/reams", label: "Risme barcode", icon: FileBarChart2 },
];
const ADMIN_NAV = [
  { to: "/admin/users", label: "Utenze", icon: Users },
  { to: "/operator", label: "Coda conferimenti", icon: ClipboardList },
  { to: "/admin/reams", label: "Risme barcode", icon: FileBarChart2 },
  { to: "/admin/settings", label: "Impostazioni", icon: Settings },
];

function navFor(role) {
  if (role === "admin") return ADMIN_NAV;
  if (role === "operator") return OPERATOR_NAV;
  return CITIZEN_NAV;
}

const roleLabel = (r) => ({
  admin: "Amministratore",
  operator: "Operatore Comune",
  citizen: "Cittadino",
  business: "Azienda",
}[r] || r);

export default function Layout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const cfg = useConfig();
  const nav = useNavigate();
  const loc = useLocation();
  const items = navFor(user?.role);

  return (
    <div className="min-h-screen bg-background">
      {/* Top nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center gap-6">
          <Link to="/app" className="flex items-center gap-2.5" data-testid="brand-link">
            <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Recycle className="w-5 h-5 text-primary" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-semibold text-sm">{cfg.comune_name} · Rifiuti</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Servizio sperimentale</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-4">
            {items.map((it) => {
              const Icon = it.icon;
              const active = loc.pathname === it.to;
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  data-testid={`nav-${it.to.replace(/\//g, "-")}`}
                  className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                    active ? "bg-primary/10 text-primary" : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {it.label}
                </Link>
              );
            })}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="user-menu-btn" className="rounded-full gap-2">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">
                    {(user?.name || "U").charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:inline text-xs">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                  <div className="text-[10px] uppercase tracking-wider mt-1 text-primary">{roleLabel(user?.role)}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/change-password")} data-testid="menu-change-password">
                  <UserIcon className="w-4 h-4 mr-2" /> Cambia password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); nav("/login"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Page header */}
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {(title || actions) && (
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              {title && <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight">{title}</h1>}
              {subtitle && <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">{subtitle}</p>}
            </div>
            {actions && <div className="flex gap-2">{actions}</div>}
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
